/**
 * My Circles widget — multi-circle aware.
 *
 * When the user is in 2+ accountability circles, this compact list is the
 * dashboard surface. For each circle we show:
 *   - the circle name + focus
 *   - member avatars (first 3)
 *   - last check-in date (this user)
 *   - unread message count (rolling 7-day approximation)
 *   - quick CTA to open the circle's chat page
 *
 * When the user is in 0 or 1 circles, the dashboard renders the original
 * single-circle widget instead — so we don't disrupt the existing UX for
 * sisters who haven't opted into multi-circle membership.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Users, MessageCircle, Plus, Calendar, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const FOCUS_LABELS = {
  career_leadership: 'Career & Leadership',
  marriage_relationship: 'Marriage & Relationship',
  motherhood: 'Motherhood',
  health_wellness: 'Health & Wellness',
  faith_spirituality: 'Faith & Spirituality',
  financial_freedom: 'Financial Freedom',
  general_support: 'General Support',
  legacy_purpose: 'Legacy & Purpose',
};

const fmtRelative = (iso) => {
  if (!iso) return 'No check-in yet';
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const days = Math.floor(diffMs / 86_400_000);
    if (days <= 0) return 'Checked in today';
    if (days === 1) return 'Checked in yesterday';
    if (days < 7) return `Checked in ${days} days ago`;
    if (days < 30) return `Checked in ${Math.floor(days / 7)}w ago`;
    return `Checked in ${then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  } catch {
    return '';
  }
};

const getInitials = (name) => (name || '').split(' ').map((n) => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'S';

const MyCirclesWidget = ({ className = '' }) => {
  const [circles, setCircles] = useState(null); // null = loading
  const [joiningAnother, setJoiningAnother] = useState(false);

  const fetchCircles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/accountability-circle/my-circles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCircles(res.data?.circles || []);
    } catch (err) {
      console.error('Failed to fetch my circles:', err);
      setCircles([]);
    }
  };

  useEffect(() => {
    fetchCircles();
  }, []);

  const handleJoinAnother = async () => {
    setJoiningAnother(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/accountability-circle/join-random`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.circle) {
        toast.success(`You joined ${res.data.circle.name}. Welcome!`);
        fetchCircles();
      } else if (res.data?.error) {
        toast.error(res.data.error);
      } else {
        toast.success('Joined a new circle');
        fetchCircles();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Could not join another circle: ${detail}`);
    } finally {
      setJoiningAnother(false);
    }
  };

  if (circles === null) {
    return (
      <Card className={className}>
        <CardContent className="p-6 flex items-center justify-center text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading your circles…
        </CardContent>
      </Card>
    );
  }

  if (circles.length === 0) {
    // 0 circles — caller should render the original single-circle widget instead.
    return null;
  }

  return (
    <Card className={className} data-testid="my-circles-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Users className="h-5 w-5" />
            My Accountability Circles
            <Badge variant="outline">{circles.length}</Badge>
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleJoinAnother}
            disabled={joiningAnother}
            data-testid="join-another-circle-btn"
          >
            {joiningAnother
              ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
              : <Plus className="h-4 w-4 mr-1" />}
            Join Another
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {circles.map((c) => {
          const focus = FOCUS_LABELS[c.focus_area] || c.focus_area || 'General';
          const memberCount = c.member_count ?? c.member_ids?.length ?? 0;
          const visibleMembers = (c.members || []).slice(0, 3);
          const extra = Math.max(0, memberCount - visibleMembers.length);
          return (
            <Link
              key={c.id}
              to={`/accountability-circle/${c.id}`}
              data-testid={`my-circle-row-${c.id}`}
              className="block rounded-xl border border-gray-200 hover:border-forest/40 hover:shadow-sm transition-all px-4 py-3 bg-white"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-forest truncate">{c.name}</span>
                    {c.unread_count > 0 && (
                      <Badge className="bg-terracotta text-white text-xs px-1.5 py-0">
                        <MessageCircle className="h-3 w-3 mr-1" />
                        {c.unread_count}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {memberCount} sister{memberCount === 1 ? '' : 's'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {fmtRelative(c.last_checkin_at)}
                    </span>
                    <Badge variant="outline" className="text-xs">{focus}</Badge>
                  </div>
                </div>
                <div className="flex items-center -space-x-2 shrink-0">
                  {visibleMembers.map((m) => (
                    <Avatar key={m.id} className="h-7 w-7 ring-2 ring-white">
                      <AvatarImage src={m.profile_image} />
                      <AvatarFallback className="text-[10px] bg-purple-100 text-purple-700">
                        {getInitials(m.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {extra > 0 && (
                    <div className="h-7 w-7 ring-2 ring-white rounded-full bg-gray-100 text-gray-600 text-[10px] flex items-center justify-center">
                      +{extra}
                    </div>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400 shrink-0" />
              </div>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default MyCirclesWidget;
